export type SearchFilterKey =
  | 'from'
  | 'to'
  | 'has'
  | 'is'
  | 'in'
  | 'before'
  | 'after'
  | 'label'
  | 'tag';

export interface SearchToken {
  id: string;
  type: 'filter' | 'text';
  key?: SearchFilterKey;
  value: string;
}

const FILTER_KEYS: SearchFilterKey[] = ['from', 'to', 'has', 'is', 'in', 'before', 'after', 'label', 'tag'];

function generateId(prefix: string, index: number): string {
  return `${prefix}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

export function tokenizeSearchQuery(query: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let quote: '"' | "'" | null = null;

  for (let i = 0; i < query.length; i++) {
    const char = query[i];

    if (quote) {
      if (char === '\\' && i + 1 < query.length) {
        const nextChar = query[i + 1];
        current += char + nextChar;
        i += 1;
        continue;
      }
      current += char;
      if (char === quote) {
        quote = null;
      }
      continue;
    }

    if (char === '"' || char === '\'') {
      quote = char;
      current += char;
      continue;
    }

    if (char === ' ' || char === '\n' || char === '\t' || char === '\r') {
      if (current) {
        tokens.push(current);
        current = '';
      }
      continue;
    }

    current += char;
  }

  if (current) {
    tokens.push(current);
  }

  return tokens.filter(Boolean);
}

function stripQuotes(value: string): string {
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1);
  }
  return value;
}

export function parseSearchQuery(query: string): SearchToken[] {
  const rawTokens = tokenizeSearchQuery(query.trim());
  const tokens: SearchToken[] = [];

  rawTokens.forEach((token, index) => {
    const [key, ...rest] = token.split(':');
    if (rest.length && FILTER_KEYS.includes(key as SearchFilterKey)) {
      const valueRaw = rest.join(':');
      const value = stripQuotes(valueRaw) || '';
      if (!value) {
        return;
      }
      tokens.push({
        id: generateId('filter', index),
        type: 'filter',
        key: key as SearchFilterKey,
        value,
      });
      return;
    }

    const value = stripQuotes(token);
    if (!value) {
      return;
    }
    tokens.push({
      id: generateId('text', index),
      type: 'text',
      value,
    });
  });

  return tokens;
}

export function stringifySearchTokens(tokens: SearchToken[]): string {
  return tokens
    .map((token) => {
      if (token.type === 'filter' && token.key) {
        const needsQuotes = token.value.includes(' ');
        const value = needsQuotes ? `"${token.value}"` : token.value;
        return `${token.key}:${value}`;
      }
      if (token.value.includes(' ')) {
        return `"${token.value}"`;
      }
      return token.value;
    })
    .join(' ')
    .trim();
}

export function removeToken(tokens: SearchToken[], tokenId: string): SearchToken[] {
  return tokens.filter((token) => token.id !== tokenId);
}

export function updateToken(tokens: SearchToken[], tokenId: string, nextValue: string): SearchToken[] {
  return tokens.map((token) => (token.id === tokenId ? { ...token, value: nextValue } : token));
}

export function upsertFilterToken(tokens: SearchToken[], key: SearchFilterKey, value: string): SearchToken[] {
  const existingIndex = tokens.findIndex((token) => token.type === 'filter' && token.key === key);
  if (existingIndex >= 0) {
    const next = tokens.slice();
    next[existingIndex] = { ...next[existingIndex], value };
    return next;
  }
  return [
    ...tokens,
    {
      id: generateId('filter', tokens.length + 1),
      type: 'filter',
      key,
      value,
    },
  ];
}

export function extractFilterValues(tokens: SearchToken[]): Record<SearchFilterKey, string[]> {
  return tokens.reduce<Record<SearchFilterKey, string[]>>((acc, token) => {
    if (token.type === 'filter' && token.key) {
      if (!acc[token.key]) {
        acc[token.key] = [];
      }
      acc[token.key].push(token.value);
    }
    return acc;
  }, {
    from: [],
    to: [],
    has: [],
    is: [],
    in: [],
    before: [],
    after: [],
    label: [],
    tag: [],
  });
}
